import React, { useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";

const TEMPLATE_COLS = [
  "lead_date", "company_name", "lead_source", "lead_owner", "pipeline", "lead_stage",
  "client_poc_name", "client_poc_number", "client_poc_email", "client_head_office",
  "poc_designation", "category", "business_model", "service_interested", "fc_location",
  "closure_month", "expected_revenue", "actual_signed_date", "remarks",
  "website_url", "event_name",
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "referrer",
];

const VALID_STAGES = [
  "New", "Market Qualified Lead (MQL)", "Sales Qualified Lead (SQL)", "Junk", "Not connected",
  "Followup/Connect later", "Requirement Gathering(RFI)", "Solutioning / Proposal Preparation",
  "Proposal Submission", "Negotiation", "Agreement Signing", "Won", "Onboarding", "Live",
  "On Hold", "Lost",
];

export default function BulkImport() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("insert"); // insert | upsert

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a CSV/XLSX file");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const updatedTxt = data.updated ? ` (updated ${data.updated})` : "";
      toast.success(`Imported ${data.created} of ${data.total_rows} rows${updatedTxt}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    // Primary path: fetch from backend with auth, which sets proper Content-Disposition.
    // This works inside iframes/preview where blob-URL downloads are often blocked.
    try {
      const res = await api.get("/leads/bulk-import/template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lms-import-template.csv";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("Template download failed:", err);
      // Last-resort fallback: open the CSV in a new tab so the user can Save-As.
      const csv = TEMPLATE_COLS.join(",") + "\n";
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.message("Opened template in a new tab — use Save As to download.");
    }
  };

  return (
    <Layout title="Bulk Import" breadcrumb={["Leads", "Bulk Import"]}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <form onSubmit={submit}>
            <h3 className="text-base font-semibold text-slate-900 mb-1">Upload leads</h3>
            <p className="text-sm text-slate-500 mb-5">Accepts <code>.csv</code> or <code>.xlsx</code>. Use the template for column headers.</p>

            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="bulk-import-mode">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1">If a lead already exists</span>
              <label className={`text-xs font-medium px-3 py-1.5 rounded-full border cursor-pointer ${mode === "insert" ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"}`}>
                <input
                  data-testid="mode-insert"
                  type="radio"
                  name="bulk-mode"
                  value="insert"
                  checked={mode === "insert"}
                  onChange={() => setMode("insert")}
                  className="hidden"
                />
                Skip duplicate
              </label>
              <label className={`text-xs font-medium px-3 py-1.5 rounded-full border cursor-pointer ${mode === "upsert" ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200"}`}>
                <input
                  data-testid="mode-upsert"
                  type="radio"
                  name="bulk-mode"
                  value="upsert"
                  checked={mode === "upsert"}
                  onChange={() => setMode("upsert")}
                  className="hidden"
                />
                Update existing
              </label>
              <span className="text-xs text-slate-400 ml-1">
                {mode === "upsert"
                  ? "Matching leads (company + email/phone) will be updated with new values; empty cells are preserved."
                  : "Matching leads will be reported as duplicates."}
              </span>
            </div>

            <label
              htmlFor="bulk-file"
              className="block border-2 border-dashed border-slate-200 rounded-xl p-10 text-center hover:border-orange-300 hover:bg-orange-50/40 cursor-pointer transition-colors"
            >
              <Upload size={28} className="mx-auto text-slate-400" />
              <div className="mt-2 text-sm font-medium text-slate-800">
                {file ? file.name : "Click to choose a file"}
              </div>
              <div className="text-xs text-slate-500 mt-1">CSV or XLSX, up to ~5MB</div>
              <input
                id="bulk-file"
                data-testid="bulk-import-file"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>

            <div className="mt-5 bg-orange-50/40 border border-orange-200 rounded-lg p-4 text-xs text-slate-700 space-y-2" data-testid="bulk-import-hint">
              <div className="font-semibold text-slate-900">Optional columns</div>
              <div>
                <b className="text-slate-800">lead_stage</b> — one of: <code className="text-[11px]">{VALID_STAGES.join(", ")}</code>. Defaults to <code>New</code>.
              </div>
              <div>
                <b className="text-slate-800">lead_date</b> — date the lead came in. Formats accepted: <code>YYYY-MM-DD</code>, <code>DD/MM/YYYY</code>, <code>DD-MM-YYYY</code>, <code>DD MMM YYYY</code>. Defaults to today.
              </div>
              <div>
                <b className="text-slate-800">lead_owner</b> — email OR full name of an active user. If left blank, the lead is assigned to you (the importer). If provided but no matching user is found, the row is rejected.
              </div>
              <div>
                <b className="text-slate-800">pipeline</b> — the source pipeline / team tag (e.g. Inbound, Outbound, Partner). Free-text but typically drawn from Admin → Master Data → Pipelines. (Backwards-compat: a column named <code>sales_poc</code> is still accepted.)
              </div>
              <div>
                <b className="text-slate-800">client_head_office / business_model / service_interested / fc_location</b> — free-text opportunity details, same as the fields on the Lead Detail page.
              </div>
              <div>
                <b className="text-slate-800">closure_month</b> — expected/actual closure month, format <code>YYYY-MM</code>.
              </div>
              <div>
                <b className="text-slate-800">expected_revenue</b> — numeric value (₹).
              </div>
              <div>
                <b className="text-slate-800">actual_signed_date</b> — date the agreement was signed. Same formats as <code>lead_date</code>.
              </div>
              <div>
                <b className="text-slate-800">utm_source / utm_medium / utm_campaign / utm_content / utm_term / referrer</b> — campaign-attribution fields. All optional; free-text accepted (admin-managed values appear in Lead Detail dropdowns for consistency).
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button type="button" onClick={downloadTemplate} className="text-sm font-medium text-orange-600 hover:underline inline-flex items-center gap-1">
                <Download size={14} /> Download template
              </button>
              <button
                data-testid="bulk-import-submit"
                disabled={busy || !file}
                className="px-4 py-2 text-sm font-medium rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white inline-flex items-center gap-1.5"
              >
                {busy ? "Importing…" : <><FileSpreadsheet size={14} /> Import</>}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">Result</h3>
          {!result && <p className="text-sm text-slate-500">Upload a file to see the summary.</p>}
          {result && (
            <>
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="bg-emerald-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{result.created}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Created</div>
                </div>
                <div className="bg-sky-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-sky-700">{result.updated ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold">Updated</div>
                </div>
                <div className="bg-red-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{result.errors.length}</div>
                  <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Errors</div>
                </div>
                <div className="bg-slate-50 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">{result.total_rows}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">Rows</div>
                </div>
              </div>
              {result.errors.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Errors</div>
                  <ul className="space-y-1.5 max-h-64 overflow-y-auto text-sm">
                    {result.errors.map((e, i) => (
                      <li key={`${e.row ?? "?"}-${i}`} className="flex gap-2 items-start text-red-700 bg-red-50 rounded-md p-2">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                        <div>Row {e.row}: {e.error}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.errors.length === 0 && (
                <div className="flex items-center gap-2 text-emerald-700 text-sm bg-emerald-50 p-3 rounded-md">
                  <CheckCircle2 size={14} /> All rows imported successfully.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
